import axios from "axios";
import { logAxiosError } from "./utils";

export enum GitProvider {
  GITHUB = "github",
  GITLAB = "gitlab",
  BITBUCKET = "bitbucket",
}

export interface GitHubRepo {
  name: string;
  owner: {
    organizations_url: string;
    avatar_url: string;
    full_name: string;
  };
  full_name?: string;
}

export interface GitLabRepo {
  name: string;
  id: number;
  path_with_namespace: string;
  namespace: {
    id: number;
    name: string;
    avatar_url?: string;
    full_path: string;
  };
  avatar_url?: string;
}

export interface BitbucketRepo {
  name: string;
  full_name: string;
  uuid: string;
  links: {
    avatar: {
      href: string;
    };
  };
}

// API response types
interface GitHubApiRepo {
  name: string;
  full_name: string;
  owner: {
    organizations_url: string;
    avatar_url: string;
  } & Record<string, unknown>;
}

type GitLabApiRepo = {
  id: number;
  path_with_namespace: string;
} & Record<string, unknown>;

interface FetchUserReposOptions {
  token: string;
  provider?: GitProvider;
}

export const fetchUserRepos = async ({
  token,
  provider = GitProvider.GITHUB,
}: FetchUserReposOptions) => {
  let currentPage = 1;
  const perPage = 100;
  let hasMore = true;
  const allRepos: any[] = [];

  let url = `${import.meta.env.VITE_BACKEND_BASE_URL}/gitProvider/user/repos`;
  if (provider === GitProvider.GITLAB) {
    url += "?gitProvider=gitlab";
  } else if (provider === GitProvider.BITBUCKET) {
    url += "?gitProvider=bitbucket";
  }

  try {
    while (hasMore) {
      const response = await axios.get(url, {
        params: {
          page: currentPage,
          per_page: perPage,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.length > 0) {
        allRepos.push(...response.data);
        currentPage++;
      }
      if (response.data.length < perPage) {
        hasMore = false;
      }
    }

    return allRepos;
  } catch (err) {
    logAxiosError("Error fetching repos", err);
    throw err;
  }
};

export const getUniqueReposByOrganization = (repos: GitHubRepo[]) => {
  return repos.filter(
    (repo, index, self) =>
      index ===
      self.findIndex(
        (r) => r.owner?.organizations_url === repo.owner?.organizations_url,
      ),
  );
};
